import cn from 'classnames'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { HIERARCHY_TYPE_WITH_FILTER_BY_PARTNER, IContact } from 'clickNgo/entities/contact'
import { fetchContactsByTopPartnerId } from 'clickNgo/entities/contact/api'
import { IPartner } from 'clickNgo/entities/partner/models'
import { InfoBlock, InfoBlockRounded, InfoBlockType } from 'cng-common/components/infoBlock'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { SimpleTable, usePagination } from 'cng-common/components/simpleTable'
import { useGetFormDataField } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components'
import { Input } from 'core-components/library/inputs'

import { CreateContactFormContent } from '../../../../../configs'
import { IContactFormKeys, TDuplicateHPRole } from '../../../../../models'
import { contactCreatorTranslation } from '../../../../../translation'

import { contactTableColumns } from './configs'
import { sortContactListByHPRoles } from './helpers'

import styles from './PartnerHPRoles.module.scss'

interface IProps {
  partner: IPartner
  formKeys: IContactFormKeys
  onLoading: (isLoading: boolean) => void
}

export const PartnerHPRoles = memo<IProps>(({ partner, onLoading, formKeys }) => {
  const [duplicateHPRoles] = useGetFormDataField<TDuplicateHPRole[]>(
    formKeys.settings,
    CreateContactFormContent.fields.duplicateHPRoles.field
  )

  const [searchString, setSearchString] = useState('')
  const [contacts, setContacts] = useState<IContact[]>([])
  const [filteredContacts, setFilteredContacts] = useState<IContact[]>([])
  const { current: pageSize } = useRef(5)
  const { onPageChange, page, paginatedList, total } = usePagination(filteredContacts, pageSize)

  const hasDuplicateHPRole = Boolean(duplicateHPRoles.length)

  const hpRolesDuplicateWarning = useMemo(
    () => (
      <Translation
        text={contactCreatorTranslation.warnings.hasHPRolesDuplicates}
        replaceParams={[
          { key: '{roles}', value: duplicateHPRoles.map(({ role }) => role).join(', ') },
        ]}
        span={true}
      />
    ),
    [duplicateHPRoles]
  )

  const renderContactHeader = useCallback(
    () => (
      <SimpleTable.HeaderRow>
        {...contactTableColumns.map((column) => (
          <SimpleTable.HeaderCell key={column.columnField as string} style={column.styles}>
            <Translation text={column.columnTitle} span />
          </SimpleTable.HeaderCell>
        ))}
      </SimpleTable.HeaderRow>
    ),
    []
  )

  const renderContactBody = useCallback(
    () =>
      paginatedList.map((contact, index) => (
        <SimpleTable.BodyRow
          key={contact.contactId}
          className={cn({
            [styles.filled]: index % 2,
            [styles.duplicate]: duplicateHPRoles?.some((el) => contact.hpRoles.includes(el.role)),
          })}
        >
          {...contactTableColumns.map((column) => {
            const field = column.columnField
            const value = contact[field] as string[] & string

            if (column.render) {
              return (
                <SimpleTable.BodyCell key={column.columnField as string}>
                  {column.render(contact)}
                </SimpleTable.BodyCell>
              )
            }

            return (
              <SimpleTable.BodyCell key={column.columnField as string}>
                {<>{value || '-'}</>}
              </SimpleTable.BodyCell>
            )
          })}
        </SimpleTable.BodyRow>
      )),
    [duplicateHPRoles, paginatedList]
  )

  useEffect(() => {
    if (partner.topPartnerId) {
      onLoading(true)
      const payload = HIERARCHY_TYPE_WITH_FILTER_BY_PARTNER.includes(partner.hierarchyType)
        ? partner.partnerId
        : partner.topPartnerId
      fetchContactsByTopPartnerId(payload)
        .then((result) => {
          const sortedContacts = result.sort(sortContactListByHPRoles)
          setContacts(sortedContacts)
        })
        .finally(() => {
          onLoading(false)
        })
    }
  }, [onLoading, partner.hierarchyType, partner.partnerId, partner.topPartnerId])

  useEffect(() => {
    setFilteredContacts(
      contacts
        .filter((item) => {
          return item.hpRoles.some((value) =>
            value.toLowerCase().includes(searchString.toLowerCase())
          )
        })
        .sort((contactA: IContact, contactB: IContact) => {
          if (duplicateHPRoles?.find((el) => contactB.hpRoles.includes(el.role))) {
            return 1
          }

          if (duplicateHPRoles?.find((el) => contactA.hpRoles.includes(el.role))) {
            return -1
          }

          return 0
        })
    )
  }, [contacts, duplicateHPRoles, searchString])

  return (
    <div className={styles.tableWrapper}>
      <RenderWhen condition={hasDuplicateHPRole}>
        <InfoBlock
          text={hpRolesDuplicateWarning}
          type={InfoBlockType.Danger}
          rounded={InfoBlockRounded.Medium}
        />
      </RenderWhen>
      <RenderWhen condition={!hasDuplicateHPRole}>
        <div className={styles.search}>
          <Input
            label={<Translation text={'cng-contact_creator-partner_hp_roles-search'} span={true} />}
            value={searchString}
            onChange={setSearchString}
            disabled={hasDuplicateHPRole}
          />
        </div>
      </RenderWhen>
      <SimpleTable
        className={cn(styles.table, styles.compact, styles.partnerHPRolesTable)}
        placeholderColspan={3}
      >
        {renderContactHeader()}
        {renderContactBody()}
      </SimpleTable>
      <RenderWhen condition={total > pageSize}>
        <div className={styles.tableFooter}>
          <SimpleTable.Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            classes={{ container: styles.pagination, button: styles.paginationButton }}
            onPageChange={onPageChange}
          />
        </div>
      </RenderWhen>
    </div>
  )
})

PartnerHPRoles.displayName = 'PartnerHPRoles'
